import React, { useState, useEffect, Fragment, useContext } from 'react';
import * as yup from 'yup';
import { I18N, I18NConsumer, GlobalContext } from 'commons';
import { baseUrl, orgId, Ajax, setPageTitle } from 'utils';
import getProfileValidationSchema from 'components/forms/my-profile/getValidationSchema';
import { Formik } from 'formik';
import FieldErrorMessage from 'components/forms/FieldErrorMessage';
import cs from 'classnames';
import ErrorBox from 'components/forms/TopErrorBox'
import Alert from 'commons/Alert'

const passwordExpired = sessionStorage.getItem('p_expired') === 'Y';

const ResetPassword = () => {
  const {
    organization: { orgName, userStatus, isFedRAMPCustomer },
    account: { CSRF_TOKEN_FOR_SESSION: csrfToken = '', name: accountName = '', id: accountId = 0 },
  } = useContext(GlobalContext);
  const [passwordNotice, setPasswordNotice] = useState('');
  const [oldPassword, setOldPassword] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [alert, setAlert] = useState({});
  const [isResetSucc, setIsResetSucc] = useState(false);
  useEffect(() => {
    if (accountId) {
      window.location.href = `${baseUrl}/manage/${orgId}/${accountId}#/home`;
    }
    if (passwordExpired && !csrfToken) {
      window.location.replace(`${baseUrl}/${orgId}/login`);
    }
    if (isFedRAMPCustomer) {
      setPasswordNotice('aware.register.passwordNotice.fedRamp');
    } else {
      setPasswordNotice(userStatus === 'public' ? 'aware.register.passwordNotice' : 'aware.register.passwordNotice2');
    }
  }, []);
  const profileValidationSchema = getProfileValidationSchema({ userStatus, isFedRAMPCustomer });
  let schema = {
    password: profileValidationSchema.password,
    confirmPassword: yup
      .string()
      .trim()
      .required('jquery.validation.field.required, ssp.register.confirmpassword')
      .test('passwords-match', 'ssp.password.confirm.invalid', value => value === password)
  };
  if (passwordExpired) {
    schema = {
      oldPassword: yup
        .string()
        .trim()
        .required('jquery.validation.field.required, ssp.changepwd.oldpwd'),
      ...schema
    };
  }
  const resetPassword = (values, actions) => {
    const url = `/${orgId}/forgotPassword`;
    const data = new FormData();
    data.append('password', values.password);
    Ajax.post(url, data).then(({ data: res }) => {
      actions.setSubmitting(false);
      const { status, errorCode } = res;
      if (status === 'timeout') {
        window.location.replace(`${baseUrl}/${orgId}/login`);
      } else if (status === '1') {
        setAlert({
          ...alert,
          type: 'success',
          msg: 'ssp.resetpwd.succ'
        });
        setIsResetSucc(true);
      } else if (errorCode) {
        setAlert({
          ...alert,
          type: 'danger',
          msg: errorCode
        });
      }
    }).catch(err => {
      actions.setSubmitting(false);
    });
  };

  const expiredPassword = (values, actions) => {
    const url = `/${orgId}/changePassword`;
    const data = new FormData();
    data.append('loginId', values.accountName);
    data.append('oldPassword', values.oldPassword);
    data.append('newPassword', values.password);
    Ajax.post(url, data, {
      headers: {
        'X-CSRF-TOKEN': csrfToken,
      }
    }).then(({ data: res }) => {
      actions.setSubmitting(false);
      const { status, errorCode } = res;
      if (status === 'timeout') {
        window.location.replace(`${baseUrl}/${orgId}/login`);
      } else if (status === '1') {
        sessionStorage.setItem('p_expired', 'N');
        window.location.replace(`${baseUrl}/${orgId}/login`);
      } else if (errorCode) {
        setAlert({
          ...alert,
          type: 'danger',
          msg: errorCode
        });
      }
    }).catch(err => {
      actions.setSubmitting(false);
    });
  };

  return (
    <div className="container reset-password">
      <I18NConsumer>
        {(i18n) => setPageTitle(`${orgName} - ${i18n['ssp.resetpwd.title']}`)}
      </I18NConsumer>
      <div className="well ssp-well">
        <div className="offset-md-1 col-md-11">
          <div>
            <h1><I18N>ssp.resetpwd.title</I18N></h1>
            {
              passwordExpired && accountName &&
              <p className="text-muted"><I18N>ssp.login.password.expiration</I18N></p>
            }
          </div>
          {
            alert.msg &&
            <div className="row">
              <div className="col-md-8">
                <Alert type={alert.type || 'warning'} message={alert.msg} role="alert" />
              </div>
            </div>
          }
          {
            !isResetSucc &&
            <Formik
              initialValues={{ accountName, oldPassword, password, confirmPassword }}
              validationSchema={yup.object().shape(schema)}
              onSubmit={(values, actions) => {
                passwordExpired ? expiredPassword(values, actions) : resetPassword(values, actions);
              }}
            >
              {({
                values,
                errors,
                touched,
                handleBlur,
                handleChange,
                handleSubmit,
                isSubmitting
              }) => (
                <form
                  className="form-horizontal margin30-T"
                  name="resetPasswordForm"
                  autoComplete="off"
                  onSubmit={e => { e.preventDefault(); }}
                  eb-test="ssp_reset_password_form"
                >
                  <p className="text-muted"><I18N>form.fields.are.required</I18N></p>
                  <ErrorBox errors={errors} isSubmitting={isSubmitting} />
                  {
                    passwordExpired && accountName &&
                    <Fragment>
                      <div className="form-group row">
                        <div className="col-md-8">
                          <label htmlFor="accountName" className="col-form-label">
                            <span><I18N>ssp.register.username</I18N></span>:
                          </label>
                          <p id="accountName">{accountName}</p>
                        </div>
                      </div>
                      <div className="form-group row">
                        <div className="col-md-8">
                          <label htmlFor="oldPassword" className="col-form-label">
                            <span><I18N>ssp.changepwd.oldpwd</I18N></span>
                            <span aria-hidden="true" className="required-star">*</span>
                            <span className="sr-only"><I18N>information.label.required</I18N></span>
                          </label>
                          <input
                            value={values.oldPassword}
                            type="password"
                            className={cs('form-control', { valid_error: errors.oldPassword && touched.oldPassword })}
                            aria-invalid={errors.oldPassword && touched.oldPassword}
                            id="oldPassword"
                            name="oldPassword"
                            maxLength="64"
                            onChange={e => {
                              setOldPassword(e.target.value);
                              handleChange(e);
                            }}
                            onBlur={handleBlur}
                            required
                            eb-test="ssp_reset_password_field_current_password"
                          />
                          {
                            touched.oldPassword && errors.oldPassword &&
                            <FieldErrorMessage id="oldPwdInvaildMsg" aria-live="assertive"><I18N>{errors.oldPassword}</I18N></FieldErrorMessage>
                          }
                        </div>
                      </div>
                    </Fragment>
                  }
                  <div className="form-group row">
                    <div className="col-md-8">
                      <label htmlFor="password" className="col-form-label">
                        <span><I18N>ssp.register.password</I18N></span>
                        <span aria-hidden="true" className="required-star">*</span>
                        <span className="sr-only"><I18N>information.label.required</I18N></span>
                      </label>
                      <input
                        type="password"
                        className={cs('form-control', { valid_error: errors.password && touched.password })}
                        aria-describedby="passwordExpiration"
                        aria-invalid={errors.password && touched.password}
                        id="password"
                        name="password"
                        value={values.password}
                        onChange={e => {
                          setPassword(e.target.value);
                          handleChange(e);
                        }}
                        onBlur={handleBlur}
                        maxLength="64"
                        minLength={isFedRAMPCustomer ? '12' : '8'}
                        required
                        eb-test="ssp_reset_password_field_password"
                      />
                      {
                        touched.password && errors.password &&
                      <FieldErrorMessage id="pwdInvaildMsg" aria-live="assertive"><I18N>{errors.password}</I18N></FieldErrorMessage>
                      }
                    </div>
                    <div id="passwordDesc" className="col-md-4 align-self-end">
                      <div className="form-text small" id="passwordExpiration">
                        <I18N>{passwordNotice}</I18N>
                      </div>
                    </div>
                  </div>

                  <div className="form-group row">
                    <div className="col-md-8">
                      <label htmlFor="confirmPassword" className="col-form-label">
                        <span><I18N>ssp.register.confirmpassword</I18N></span>
                        <span aria-hidden="true" className="required-star">*</span>
                        <span className="sr-only"><I18N>information.label.required</I18N></span>
                      </label>
                      <input
                        type="password"
                        className={cs('form-control', { valid_error: errors.confirmPassword && touched.confirmPassword })}
                        aria-invalid={errors.confirmPassword && touched.confirmPassword}
                        id="confirmPassword"
                        name="confirmPassword"
                        value={values.confirmPassword}
                        onChange={e => {
                          setConfirmPassword(e.target.value);
                          handleChange(e);
                        }}
                        onBlur={handleBlur}
                        maxLength="64"
                        required
                        eb-test="ssp_reset_password_field_confirm_password"
                      />
                      {
                        touched.confirmPassword && errors.confirmPassword &&
                      <FieldErrorMessage id="confirmPwdInvaildMsg" aria-live="assertive"><I18N>{errors.confirmPassword}</I18N></FieldErrorMessage>
                      }
                    </div>
                  </div>

                  <div className="form-group row">
                    <div className="offset-md-3 col-md-5 mt-3">
                      <button
                        type="button"
                        onClick={handleSubmit}
                        className="btn btn-primary btn-block"
                        disabled={isSubmitting}
                        eb-test="ssp_reset_password_button_submit"
                      >
                        <I18N>button.save</I18N>
                      </button>
                    </div>
                  </div>
                </form>
              )}
            </Formik>
          }
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
